import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader, EmptyState, StatusBadge } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Plus, Scale } from "lucide-react";

export default function CasesListPage() {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/cases").then((r) => setCases(r.data)).finally(() => setLoading(false));
  }, []);

  const filtered = cases.filter(
    (c) => c.title.toLowerCase().includes(q.toLowerCase()) || c.case_type.toLowerCase().includes(q.toLowerCase()),
  );

  const tone = (s) => ({ Active: "active", Closed: "neutral", Pending: "warn", Intake: "neutral" }[s] || "neutral");

  return (
    <div data-testid="cases-list-page">
      <PageHeader
        eyebrow="Le Dossier"
        title="Cases"
        description="Every dispute, every grievance — each organised as its own dossier."
        action={
          <Link to="/app/cases/new">
            <Button className="rounded-none bg-[#2A0F5A] hover:bg-[#3D1F85] text-[#FAFAF8]" data-testid="cases-new-btn">
              <Plus size={14} className="mr-1.5" /> New case
            </Button>
          </Link>
        }
      />

      <div className="mb-6 max-w-sm">
        <Input
          placeholder="Search cases…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="rounded-none border-[#E8E3F0] bg-white focus-visible:ring-0 focus-visible:border-[#2A0F5A]"
          data-testid="cases-search-input"
        />
      </div>

      {loading ? (
        <div className="label-eyebrow">Loading…</div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Scale}
          title="No cases yet"
          description="Open your first case to begin structuring the record."
          action={
            <Link to="/app/cases/new">
              <Button className="rounded-none bg-[#2A0F5A] hover:bg-[#3D1F85] text-[#FAFAF8]" data-testid="empty-cases-new-btn">
                <Plus size={14} className="mr-1.5" /> New case
              </Button>
            </Link>
          }
        />
      ) : (
        <div className="border border-[#E8E3F0] bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E8E3F0]">
                <th className="text-left px-6 py-3 label-eyebrow">Title</th>
                <th className="text-left px-6 py-3 label-eyebrow hidden md:table-cell">Type</th>
                <th className="text-left px-6 py-3 label-eyebrow hidden md:table-cell">Jurisdiction</th>
                <th className="text-left px-6 py-3 label-eyebrow">Status</th>
                <th className="text-left px-6 py-3 label-eyebrow hidden md:table-cell">Updated</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr
                  key={c.id}
                  className="border-b border-[#E8E3F0] hover:bg-[#FAFAF8] cursor-pointer"
                  data-testid={`case-row-${c.id}`}
                >
                  <td className="px-6 py-4">
                    <Link to={`/app/cases/${c.id}`} className="font-serif text-lg">{c.title}</Link>
                    <div className="text-xs text-[#4A5568] md:hidden mt-0.5">{c.case_type} · {c.jurisdiction}</div>
                  </td>
                  <td className="px-6 py-4 hidden md:table-cell">{c.case_type}</td>
                  <td className="px-6 py-4 hidden md:table-cell">{c.jurisdiction}</td>
                  <td className="px-6 py-4"><StatusBadge tone={tone(c.status)}>{c.status}</StatusBadge></td>
                  <td className="px-6 py-4 hidden md:table-cell text-xs text-[#4A5568]">
                    {new Date(c.updated_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
